import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["type", "children", "swimlaneChildrenConfig"];

var _jsxFileName = "/home/bamboo/bamboo-agent-home/xml-data/build-dir/DLS-WEB7-JOB1/src/components/Carousel/CarouselSwimlane.js",
    _this = this;

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight } from '@dls/assets/icons';
import { Wrapper, CarouselWrapper, CarouselContainer, CarouselSlot, ArrowButton, ArrowButtonContainer, LeftArrowOverlay, RightArrowOverlay } from './style';
import { testIdProps } from '../../helpers';
import useDevice from '../../core/hooks/useDevice';
/**
 * This padding is used to give enough room to display the card shadow inside the carousel swimlane
 */

export var CARD_CAROUSEL_PADDING = 7;
/**
 * Gap between child items in the carousel swimlane
 */

var CAROUSEL_CHILD_ITEM_GAP = 16;
/**
 * Default width for carousel child item
 */

var DEFAULT_CAROUSEL_CHILD_WIDTH = 290;
/**
 * Display a preview of 40px of the next card in mobile breakpoint
 */

var CAROUSEL_NEXT_CHILD_PREVIEW_WIDTH_IN_MOBILE = 40;
/**
 * Default carousel children count for different breakpoints
 */

var DEFAULT_CAROUSEL_CHILDREN_COUNT = {
  sm: 2,
  md: 3,
  lg: 3,
  xl: 3
};
/**
 * In carousel scrollLeft value calculation, there is a rounding off error.
 * https://stackoverflow.com/questions/64502303/javascript-element-scrollleft-cant-handle-decimals
 * We need to factor in this when calculating carousel child width
 */

var CAROUSEL_SCROLL_LEFT_ROUNDING_OFF_ERROR_PX_VALUE = 1.5;
/**
 * Get fully visible child items count in the carousel container. These fully visible items would be
 * scrolled away when clicked on the arrow button.
 *
 * @param {number} currentIndex - Current index
 * @param {number} singleCarouselChildItemWidth - Single carousel child item width
 * @param {number} carouselContainerWidth - Carousel container width
 * @param {number} arrowOverlayWidth - Arrow overlay width
 * @returns Fully visible child items count
 */

var getFullyVisibleChildItemsCount = function getFullyVisibleChildItemsCount(_ref) {
  var currentIndex = _ref.currentIndex,
      singleCarouselChildItemWidth = _ref.singleCarouselChildItemWidth,
      carouselContainerWidth = _ref.carouselContainerWidth,
      arrowOverlayWidth = _ref.arrowOverlayWidth;
  var fullyVisibleItemsCount = 0;

  var calculateFullyVisibleItemsCount = function calculateFullyVisibleItemsCount(carouselContainerWidthExcludingOverlay) {
    var carouselChildItemsWidthIncludingGapBetweenItems = 0;

    while (carouselContainerWidthExcludingOverlay > carouselChildItemsWidthIncludingGapBetweenItems) {
      var carouselChildItemsWidthExcludingLastGap = carouselChildItemsWidthIncludingGapBetweenItems + singleCarouselChildItemWidth;

      if (carouselChildItemsWidthExcludingLastGap < carouselContainerWidthExcludingOverlay) {
        fullyVisibleItemsCount++;
      }

      carouselChildItemsWidthIncludingGapBetweenItems = carouselChildItemsWidthExcludingLastGap + CAROUSEL_CHILD_ITEM_GAP;
    }
  };

  if (currentIndex <= 1) {
    var carouselContainerWidthExcludingRightOverlay = carouselContainerWidth - arrowOverlayWidth;
    calculateFullyVisibleItemsCount(carouselContainerWidthExcludingRightOverlay);
  } else {
    var carouselContainerWidthExcludingLeftAndRightOverlay = carouselContainerWidth - arrowOverlayWidth * 2 - CAROUSEL_CHILD_ITEM_GAP;
    calculateFullyVisibleItemsCount(carouselContainerWidthExcludingLeftAndRightOverlay);
  }

  return fullyVisibleItemsCount;
};

var CarouselSwimlane = function CarouselSwimlane(_ref2) {
  var _carousel$current4;

  var type = _ref2.type,
      children = _ref2.children,
      swimlaneChildrenConfig = _ref2.swimlaneChildrenConfig,
      rest = _objectWithoutProperties(_ref2, _excluded);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      currentIndex = _useState2[0],
      setCurrentIndex = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      canNext = _useState4[0],
      setCanNext = _useState4[1];

  var _useState5 = useState(true),
      _useState6 = _slicedToArray(_useState5, 2),
      canPrev = _useState6[0],
      setCanPrev = _useState6[1];

  var carousel = useRef(null);
  var carouselChild = useRef(null);
  var leftArrowOverlayRef = useRef(null);
  /**
   * When user clicks right arrow, we store the left scroll value for that particular index in this object map.
   * So when user clicks left arrow later, we can easily set to the relevant scroll left value instead of calculating again.
   */

  var carouselLeftScrollValuesForNavigatedIndicesRef = useRef({});

  var _useDevice = useDevice(),
      isMobile = _useDevice.isMobile,
      isTablet = _useDevice.isTablet,
      isLandscapeTablet = _useDevice.isLandscapeTablet,
      isDesktop = _useDevice.isDesktop,
      isTV = _useDevice.isTV;

  var numOfPage = children && children.length;

  var movePrev = function movePrev() {
    if (currentIndex > 0) {
      setCurrentIndex(function (prevState) {
        return prevState - 1;
      });
    }
  };

  var moveNext = function moveNext() {
    var _carousel$current;

    var maxScrollWidth = carousel === null || carousel === void 0 ? void 0 : (_carousel$current = carousel.current) === null || _carousel$current === void 0 ? void 0 : _carousel$current.scrollWidth;
    if (!canNext) return false;

    if (carousel.current !== null && carousel.current.scrollLeft < maxScrollWidth) {
      setCurrentIndex(function (prevState) {
        return prevState + 1;
      });
    }
  };

  var determineSlideNextPossible = useCallback(function (carouselLeftScrollValue) {
    var _carousel$current2, _carousel$current3;

    var maxScrollWidth = carousel === null || carousel === void 0 ? void 0 : (_carousel$current2 = carousel.current) === null || _carousel$current2 === void 0 ? void 0 : _carousel$current2.scrollWidth;
    var carouselContainerWidth = carousel === null || carousel === void 0 ? void 0 : (_carousel$current3 = carousel.current) === null || _carousel$current3 === void 0 ? void 0 : _carousel$current3.clientWidth;
    var totalScrolledWidthIncludingCarouselContainerWidth = carouselLeftScrollValue + carouselContainerWidth;
    var canNext = totalScrolledWidthIncludingCarouselContainerWidth < maxScrollWidth;
    setCanNext(canNext);
  }, [carousel === null || carousel === void 0 ? void 0 : (_carousel$current4 = carousel.current) === null || _carousel$current4 === void 0 ? void 0 : _carousel$current4.scrollWidth]);
  var determineSlidePrevPossible = useCallback(function () {
    setCanPrev(currentIndex > 0);
  }, [currentIndex]);
  /**
   * Get arrow overlay width. Both left and right arrow are having same width (1 grid column width).
   * So, here we return the left arrow width. If element is not available in ref yet, we return 0.
   * @returns {number} - Arrow overlay width
   */

  var getArrowOverlayWidth = useCallback(function () {
    var _leftArrowOverlayRef$;

    return ((_leftArrowOverlayRef$ = leftArrowOverlayRef.current) === null || _leftArrowOverlayRef$ === void 0 ? void 0 : _leftArrowOverlayRef$.getBoundingClientRect().width) || 0;
  }, []);
  /**
   * Scroll carousel content from left edge with the defined px value. When value is having decimals,
   * 'scrollLeft' property is ignoring the fractional point. Because of that, it is scrolled less than expected.
   * So here we round up to nearest integer and set.
   * https://stackoverflow.com/questions/64502303/javascript-element-scrollleft-cant-handle-decimals
   */

  var scrollCarousel = useCallback(function (carouselLeftScrollValue) {
    carousel.current.scrollLeft = Math.ceil(carouselLeftScrollValue);
  }, []);
  useEffect(function () {
    // When screen size changed, reset the stored left scroll values for the indices and
    // slide to first item
    carouselLeftScrollValuesForNavigatedIndicesRef.current = {}; // Reset overflow

    carousel.current.style.overflow = 'auto';
    setCurrentIndex(0);
  }, [isMobile, isTablet, isLandscapeTablet, isDesktop, isTV]);
  useEffect(function () {
    var _carouselChild$curren, _carousel$current5;

    var carouselLeftScrollValueForCurrentIndex = carouselLeftScrollValuesForNavigatedIndicesRef.current[currentIndex]; // If carousel left scrolling value for the current index is already stored in the object map,
    // use the stored value to scroll, instead of recalculating

    if (carouselLeftScrollValueForCurrentIndex) {
      scrollCarousel(carouselLeftScrollValueForCurrentIndex);
      determineSlideNextPossible(carouselLeftScrollValueForCurrentIndex);
      determineSlidePrevPossible();
      return;
    }

    var singleCarouselChildItemWidth = carouselChild === null || carouselChild === void 0 ? void 0 : (_carouselChild$curren = carouselChild.current) === null || _carouselChild$curren === void 0 ? void 0 : _carouselChild$curren.getBoundingClientRect().width;
    var carouselContainerWidth = carousel === null || carousel === void 0 ? void 0 : (_carousel$current5 = carousel.current) === null || _carousel$current5 === void 0 ? void 0 : _carousel$current5.getBoundingClientRect().width;
    var arrowOverlayWidth = getArrowOverlayWidth();
    var fullyVisibleItemsCount = getFullyVisibleChildItemsCount({
      currentIndex: currentIndex,
      singleCarouselChildItemWidth: singleCarouselChildItemWidth,
      carouselContainerWidth: carouselContainerWidth,
      arrowOverlayWidth: arrowOverlayWidth
    });
    var carouselChildItemsWidth = fullyVisibleItemsCount * singleCarouselChildItemWidth;
    var carouselChildItemsGap = fullyVisibleItemsCount * CAROUSEL_CHILD_ITEM_GAP;
    var carouselChildItemsWidthAndGap = carouselChildItemsWidth + carouselChildItemsGap;

    if (carousel !== null && carousel.current !== null) {
      var carouselLeftScrollValue = 0;

      switch (currentIndex) {
        case 0:
          carouselLeftScrollValue = 0;
          break;

        case 1:
          carouselLeftScrollValue = carouselChildItemsWidthAndGap - arrowOverlayWidth - CAROUSEL_CHILD_ITEM_GAP + CARD_CAROUSEL_PADDING;
          break;

        default:
          carouselLeftScrollValue = carousel.current.scrollLeft + carouselChildItemsWidthAndGap;
          break;
      }

      scrollCarousel(carouselLeftScrollValue); // Store carousel left scrolling value for the current index

      var updatedLeftScrollValuesObjMap = Object.assign({}, carouselLeftScrollValuesForNavigatedIndicesRef.current);
      updatedLeftScrollValuesObjMap[currentIndex] = carouselLeftScrollValue;
      carouselLeftScrollValuesForNavigatedIndicesRef.current = updatedLeftScrollValuesObjMap;
      determineSlideNextPossible(carouselLeftScrollValue);
      determineSlidePrevPossible();
    }
  }, [currentIndex, determineSlidePrevPossible, determineSlideNextPossible, getArrowOverlayWidth, scrollCarousel]);

  var getCarouselSlotStyle = function getCarouselSlotStyle() {
    var _carousel$current6;

    var carouselSlotStyle = {};
    var carouselContainerWidth = ((_carousel$current6 = carousel.current) === null || _carousel$current6 === void 0 ? void 0 : _carousel$current6.getBoundingClientRect().width) || 0;
    var childWidth = DEFAULT_CAROUSEL_CHILD_WIDTH;

    if (!isMobile) {
      var childrenCount = DEFAULT_CAROUSEL_CHILDREN_COUNT.lg;

      if (isTablet) {
        childrenCount = (swimlaneChildrenConfig === null || swimlaneChildrenConfig === void 0 ? void 0 : swimlaneChildrenConfig.sm) || DEFAULT_CAROUSEL_CHILDREN_COUNT.sm;
      } else if (isLandscapeTablet) {
        childrenCount = (swimlaneChildrenConfig === null || swimlaneChildrenConfig === void 0 ? void 0 : swimlaneChildrenConfig.md) || DEFAULT_CAROUSEL_CHILDREN_COUNT.md;
      } else if (isDesktop) {
        childrenCount = (swimlaneChildrenConfig === null || swimlaneChildrenConfig === void 0 ? void 0 : swimlaneChildrenConfig.lg) || DEFAULT_CAROUSEL_CHILDREN_COUNT.lg;
      } else {
        childrenCount = (swimlaneChildrenConfig === null || swimlaneChildrenConfig === void 0 ? void 0 : swimlaneChildrenConfig.xl) || DEFAULT_CAROUSEL_CHILDREN_COUNT.xl;
      }

      var arrowOverlayWidth = getArrowOverlayWidth();
      var carouselContainerWidthExcludingLeftAndRightOverlay = 0;

      if (arrowOverlayWidth > 0 && carouselContainerWidth > 0) {
        carouselContainerWidthExcludingLeftAndRightOverlay = carouselContainerWidth - (arrowOverlayWidth + CAROUSEL_CHILD_ITEM_GAP) * 2;
      }

      if (carouselContainerWidthExcludingLeftAndRightOverlay > 0) {
        var gapBetweenChildren = CAROUSEL_CHILD_ITEM_GAP * (childrenCount - 1);
        var childrenContainerWidthExcludingGapBetweenChildren = carouselContainerWidthExcludingLeftAndRightOverlay + CAROUSEL_SCROLL_LEFT_ROUNDING_OFF_ERROR_PX_VALUE - gapBetweenChildren;
        childWidth = childrenContainerWidthExcludingGapBetweenChildren / childrenCount;
      }
    } else {
      if (carouselContainerWidth > 0) {
        childWidth = carouselContainerWidth - CAROUSEL_CHILD_ITEM_GAP - CAROUSEL_NEXT_CHILD_PREVIEW_WIDTH_IN_MOBILE;
      }
    }

    carouselSlotStyle.width = childWidth;
    return carouselSlotStyle;
  };

  var renderChildren = function renderChildren(type) {
    var carouselChildren = React.Children.toArray(children);
    var renderedChildren;

    if (carouselChildren.length) {
      renderedChildren = carouselChildren;
      var carouselSlotStyle = getCarouselSlotStyle();
      return renderedChildren.map(function (child, index) {
        var props = {};
        if (index === 0) props.ref = carouselChild;
        return /*#__PURE__*/React.createElement(CarouselSlot, _extends({
          key: index,
          type: type
        }, props, {
          style: Object.assign({}, carouselSlotStyle),
          __self: _this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 346,
            columnNumber: 11
          }
        }), child);
      });
    }

    return null;
  };

  var showLeftArrow = canPrev && numOfPage > 1;
  var showRightArrow = canNext && numOfPage > 1;
  return /*#__PURE__*/React.createElement("div", _extends({}, testIdProps(rest), {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 364,
      columnNumber: 5
    }
  }), /*#__PURE__*/React.createElement(Wrapper, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 365,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(LeftArrowOverlay, {
    xs: 1,
    ref: leftArrowOverlayRef,
    showArrow: showLeftArrow,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 366,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(ArrowButtonContainer, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 371,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(ArrowButton, {
    "data-testid": "arrow-left",
    onClick: movePrev,
    inside: true,
    showArrow: showLeftArrow,
    position: "relative",
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 372,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(ArrowLeft, {
    size: 25,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 379,
      columnNumber: 15
    }
  })))), /*#__PURE__*/React.createElement(CarouselWrapper, {
    type: type,
    ref: carousel,
    "data-testid": "carousel-container-wrapper",
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 384,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(CarouselContainer, {
    "data-testid": "carousel-container",
    type: type,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 389,
      columnNumber: 11
    }
  }, renderChildren(type))), /*#__PURE__*/React.createElement(RightArrowOverlay, {
    xs: 1,
    showArrow: showRightArrow,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 394,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(ArrowButtonContainer, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 395,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(ArrowButton, {
    "data-testid": "arrow-right",
    onClick: moveNext,
    inside: true,
    showArrow: showRightArrow,
    position: "relative",
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 396,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(ArrowRight, {
    size: 25,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 403,
      columnNumber: 15
    }
  }))))));
};

CarouselSwimlane.defaultProps = {
  swimlaneChildrenConfig: {
    sm: 2,
    md: 3,
    lg: 3,
    xl: 3
  }
};
CarouselSwimlane.displayName = 'CarouselSwimlane';
export default CarouselSwimlane;